import { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { StoreContext } from "../../store.js";
import Select from 'react-select';

import './index.css';
import QuizOptions from "./quiz-options";
import QuizLocations from "./quiz-locations.jsx";
import Show from '../../utility/Show.jsx';
import FormError from '../../utility/FormError.jsx';
import { CircularLoader } from "../Loaders";

import { ReactComponent as WalkspanLogo } from '../../assets/walkspan-logo-2.svg';
import { ReactComponent as Arrow } from '../../assets/arrow-left-circle.svg';
import { ReactComponent as Icon } from '../../assets/quiz/icon-1.svg';
import { ReactComponent as Icon2 } from '../../assets/quiz/icon-2.svg';
import { ReactComponent as Icon3 } from '../../assets/quiz/icon-3.svg';

import { quizQuestions } from "../../constants";

const STYLE_WRAPPER = { '--quiz-result-color': '#F6CE26' };

const BOROUGH_LIST = [
  { label: 'Manhattan', value: 'Manhattan' },
  { label: 'Brooklyn', value: 'Brooklyn' },
  { label: 'Queens', value: 'Queens' },
  { label: 'Bronx', value: 'Bronx' },
  { label: 'Staten Island', value: 'Staten Island' },
];

const INITIAL_PAYLOAD = {
  choices: {
    access: 0,
    activities: 0,
    architecture: 0,
    comfort: 0,
    nature: 0,
    noise: 0,
    vibe: 0,
  },
  selectedNeighborhoodId: "",
};

const Quiz = () => {
  const { state, actions, dispatch } = useContext(StoreContext);
  const { neighborhoodList, errorMessage, quizResults } = state;
  const [currentStep, setCurrentStep] = useState(0);
  const [answerList, setAnswerList] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showLocations, setShowLocations] = useState(false);
  const [location, setLocation] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [payload, setPayload] = useState(INITIAL_PAYLOAD);

  useEffect(() => {
    actions.handleLogin();
  }, []);

  const isLastQuestion = currentStep === quizQuestions.length - 1;
  const isFirstQuestion = currentStep === 0;

  const modifiedNeighborhoodList = useMemo(() => {
    if (!neighborhoodList?.data?.length) return [];
    return neighborhoodList.data.map((n) => ({ label: n.name, value: n.id }));
  }, [neighborhoodList.data]);

  const updatePayload = useCallback((property, value) => {
    setPayload(prev => ({
      ...prev,
      choices: { ...prev.choices, [property]: value },
    }));
  }, []);

  const onOptionClick = useCallback((property, option) => {
    const isExisting = answerList.find((item) => item?.question === option.question);
    const newAnswerList = isExisting
      ? [...answerList.filter((item) => item?.question !== option.question), option]
      : [...answerList, option];
    setAnswerList(newAnswerList);
    updatePayload(property, option.answer.value);
  }, [answerList, updatePayload]);

  const hasSelectedOption = useCallback(() => {
    return answerList.find((answer) => answer.question === quizQuestions[currentStep].question);
  }, [answerList, currentStep]);

  const handleLocationChange = useCallback(async (selected) => {
    setLocation(selected);
    setNeighborhood('');
    actions.clearErrorMessage();
    await actions.fetchNeighborhoodList(selected.value);
  }, [actions]);

  const handleNeighborhoodChange = useCallback((selected) => {
    setNeighborhood(selected);
    setPayload(prev => ({ ...prev, selectedNeighborhoodId: selected.value }));
  }, []);

  const handleNext = () => {
    if (hasSelectedOption()) {
      actions.clearErrorMessage();
      if (isLastQuestion) {
        setIsSubmitted(true);
        actions.submitQuiz(payload);
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else {
      dispatch({ type: 'SET_ERROR_MESSAGE', payload: 'Please select an option' });
    }
  };

  const handleBack = () => {
    actions.clearErrorMessage();
    if (isSubmitted) {
      setIsSubmitted(false);
    } else {
      if (isFirstQuestion) {
        setShowQuiz(false);
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const handleExitMap = useCallback(() => {
    setShowLocations(false);
    setShowResult(false);
    setShowQuiz(false);
    setAnswerList([]);
    setIsSubmitted(false);
    setPayload(INITIAL_PAYLOAD);
    setLocation('');
    setNeighborhood('');
    setCurrentStep(0);
  }, []);

  const isStartDisabled =
    neighborhoodList.data.length === 0 ||
    errorMessage ||
    location === '' ||
    neighborhood === '';

  const renderQuiz = () => (
    <div className="quiz-container">
      <div
        className={`quiz-card ${showResult && 'quiz-result-card'}`}
        style={STYLE_WRAPPER}
      >
        <Show>
          <Show.When isTrue={showResult}>
            <div>
              <div className="neighborhood-type">
                Your neighborhood type is:
                <div className="type">Quiet</div>
              </div>
              <div className='search-address-input'>
                <input placeholder='Enter your location' />
              </div>
              <button
                className="quiz-btn view-locations"
                onClick={() => setShowLocations(true)}
              >
                Next
              </button>
            </div>
          </Show.When>
          <Show.Else>
            <div className="left-column">
              <div className={`quiz-card-header ${!showQuiz && 'hidden'}`}>
                <Arrow className="back-btn" onClick={handleBack} />
                <div className="current-step">
                  {currentStep + 1}/{quizQuestions.length}
                </div>
              </div>
              <div className="content">
                <div className='title'>
                  <Show>
                    <Show.When isTrue={isSubmitted}>congrats !</Show.When>
                    <Show.Else>
                      {showQuiz ? `Q ${currentStep + 1}.` : 'The Quiz'}
                    </Show.Else>
                  </Show>
                </div>
                <div className={`text ${!showQuiz && !isSubmitted ? 'first-text' : ''} ${showQuiz && !isSubmitted ? 'quiz-text' : ''}`}>
                  <Show>
                    <Show.When isTrue={isSubmitted}>You have completed the Quiz</Show.When>
                    <Show.Else>
                      {showQuiz
                        ? quizQuestions[currentStep]?.question
                        : 'Find the Neighborhood of Your Choice'
                      }
                    </Show.Else>
                  </Show>
                </div>
                <Show>
                  <Show.When isTrue={isSubmitted}><Icon3 className="icon" /></Show.When>
                  <Show.Else>
                    {showQuiz ? <Icon2 className="icon" /> : <Icon className="icon" />}
                  </Show.Else>
                </Show>
              </div>
            </div>
            <div className="right-column">
              <div className={`logo-container ${showQuiz && 'small'}`}>
                <WalkspanLogo className={`logo ${showQuiz && 'small'}`} />
              </div>
              <Show>
                <Show.When isTrue={isSubmitted}>
                  <div className="quiz-results">
                    <Show>
                      <Show.When isTrue={quizResults.isLoading}>
                        Getting quiz result...
                      </Show.When>
                      <Show.Else>
                        <Show>
                          <Show.When isTrue={quizResults.data.length > 0}>
                            <div className="total-locations">{quizResults.data.length}</div>
                            location{quizResults.data.length > 1 && 's'} matched
                          </Show.When>
                          <Show.Else>No data found</Show.Else>
                        </Show>
                      </Show.Else>
                    </Show>
                  </div>
                </Show.When>
                <Show.Else>
                  <Show>
                    <Show.When isTrue={showQuiz}>
                      <QuizOptions
                        errorMessage={errorMessage}
                        currentQuestion={quizQuestions[currentStep]}
                        onOptionClick={onOptionClick}
                        answerList={answerList}
                      />
                    </Show.When>
                    <Show.Else>
                      <div className="dropdowns">
                        <div className="input-wrapper">
                          <span className="input-label">Location, City</span>
                          <Select
                            className='quiz-select'
                            classNamePrefix='select'
                            value={location}
                            placeholder='Select location'
                            options={BOROUGH_LIST}
                            onChange={handleLocationChange}
                          />
                        </div>
                        <div className="input-wrapper">
                          <span className="input-label">Neighborhood</span>
                          <Select
                            className='quiz-select'
                            classNamePrefix='select'
                            placeholder='Select neighborhood'
                            value={neighborhood}
                            isDisabled={neighborhoodList.isLoading || neighborhoodList.data.length === 0}
                            options={modifiedNeighborhoodList}
                            onChange={handleNeighborhoodChange}
                          />
                          <Show.When isTrue={errorMessage}>
                            <FormError error={errorMessage} textColor="#ed5951" />
                          </Show.When>
                        </div>
                      </div>
                    </Show.Else>
                  </Show>
                </Show.Else>
              </Show>
              <div className="action-btns">
                <Show>
                  <Show.When isTrue={isSubmitted}>
                    <Show.When isTrue={quizResults.data.length > 0}>
                      <button className="quiz-btn see-results" onClick={() => setShowLocations(true)}>
                        <Show>
                          <Show.When isTrue={quizResults.isLoading}>
                            <CircularLoader size={'10px'} />
                          </Show.When>
                          <Show.Else>See Results</Show.Else>
                        </Show>
                      </button>
                    </Show.When>
                  </Show.When>
                  <Show.Else>
                    <Show>
                      <Show.When isTrue={showQuiz}>
                        <button className="quiz-btn" onClick={handleNext}>
                          {isLastQuestion ? 'Submit' : 'Next'}
                        </button>
                      </Show.When>
                      <Show.Else>
                        <button
                          className={`quiz-btn start-quiz ${isStartDisabled && 'disabled'}`}
                          onClick={() => setShowQuiz(true)}
                        >
                          Start Quiz
                        </button>
                        <button className="cancel-quiz">Cancel</button>
                      </Show.Else>
                    </Show>
                  </Show.Else>
                </Show>
              </div>
            </div>
          </Show.Else>
        </Show>
      </div>
    </div>
  );

  return (
    <Show>
      <Show.When isTrue={showLocations}>
        <QuizLocations locations={quizResults.data} handleExit={handleExitMap} />
      </Show.When>
      <Show.Else>
        {renderQuiz()}
      </Show.Else>
    </Show>
  );
};

export default Quiz;
